// ExistingCvMatches — "Also check our existing CVs" (F11, Shohini 29 Oct
// 2026: "when creating a JD, or for a job, I should be able to run it against
// the existing CVs"). On the EquaOS design system.
//
// ExistingCvsButton opens it from the vacancy's page; ExistingCvsOffer is the
// card shown right after a JD is created. Either way:
//   1. POST /talent-pool/jobs/:jobId/existing-cvs/match scores this school's
//      earlier applicants against the vacancy (a CV scored in the last day is
//      not scored again) and lists the best fits with a score and why;
//   2. HR ticks whom to add; POST /talent-pool/jobs/:jobId/existing-cvs/add
//      makes each one a new application for this vacancy. Their earlier
//      application stays as it was. Nobody is added or messaged until HR
//      presses Add, and nobody is messaged then either.
// @stories HIR-HR3, HIR-HR10
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { Button, Card, Checkbox, EmptyState, Modal, StatusPill } from '../../ds';

const toneOf = (score) => (score >= 75 ? 'success' : score >= 60 ? 'info' : 'warning');

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

function Reasons({ items }) {
  if (!items || !items.length) return null;
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0', display: 'grid', gap: 4 }}>
      {items.map((s, i) => (
        <li key={i} style={{ display: 'flex', gap: 8, fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
          <span aria-hidden="true" style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--text-muted)', marginTop: 8, flex: 'none' }} />
          <span>{s}</span>
        </li>
      ))}
    </ul>
  );
}

export default function ExistingCvMatches({ jobId, jobTitle, open, onClose, onAdded }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [picked, setPicked] = useState(() => new Set());
  const [adding, setAdding] = useState(false);
  const [done, setDone] = useState(null);
  // Email each one "you are also being considered for this role" (30 Oct 2026).
  const [tell, setTell] = useState(false);

  const run = useCallback(async () => {
    setLoading(true);
    setError('');
    setResult(null);
    setDone(null);
    setPicked(new Set());
    try {
      const r = await api.post(`/talent-pool/jobs/${jobId}/existing-cvs/match`, {});
      setResult(r.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.message || 'Could not check the existing CVs just now. Try again shortly.');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { if (open && jobId) run(); }, [open, jobId, run]);

  const toggle = (id) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleAdd = async () => {
    const resumeIds = Array.from(picked);
    if (!resumeIds.length) return;
    setAdding(true);
    try {
      const r = await api.post(`/talent-pool/jobs/${jobId}/existing-cvs/add`, { resumeIds, ...(tell ? { tellCandidates: true } : {}) });
      const d = r.data?.data || { added: [], alreadyOnJob: [] };
      setDone(d);
      setPicked(new Set());
      if (d.added.length) toast.success(`Added ${plural(d.added.length, 'person', 'people')} to this vacancy`);
      onAdded?.(d);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not add them just now.');
    } finally {
      setAdding(false);
    }
  };

  const addedIds = new Set((done?.added || []).map((a) => a.fromResumeId).concat((done?.alreadyOnJob || []).map((a) => a.fromResumeId)));
  const matches = result?.matches || [];

  const footer = (
    <>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center', marginRight: 'auto', fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
        <input type="checkbox" checked={tell} onChange={(e) => setTell(e.target.checked)} />
        Email them that they are being considered
      </label>
      <Button variant="ghost" onClick={onClose}>Close</Button>
      <Button onClick={handleAdd} disabled={!picked.size || adding || loading} loading={adding}>
        {picked.size ? `Add ${picked.size} to this vacancy` : 'Add to this vacancy'}
      </Button>
    </>
  );

  return (
    <div className="eq-ds">
      <Modal open={open} onClose={onClose} width="720px" title="Our existing CVs for this vacancy" footer={footer}>
        <p style={{ margin: '0 0 16px', fontSize: 'var(--fs-body-md)', color: 'var(--text-2)' }}>
          People who applied to this school for other roles, scored against the description of
          {' '}<strong>{jobTitle || 'this vacancy'}</strong>. Nobody is added until you choose.
        </p>

        {loading && (
          <div role="status" style={{ padding: '32px 0', textAlign: 'center', color: 'var(--text-muted)' }}>
            Checking this school's CVs against this vacancy…
          </div>
        )}

        {!loading && error && (
          <Card padding="16px" style={{ boxShadow: 'none' }}>
            <div role="alert" style={{ color: 'var(--status-danger-text)', fontSize: 'var(--fs-body-md)' }}>{error}</div>
          </Card>
        )}

        {!loading && !error && result && matches.length === 0 && (
          <EmptyState
            title="No good fits in our existing CVs"
            description={result.poolSize
              ? `We checked ${plural(result.considered, 'CV', 'CVs')} from other roles; none fits this vacancy well.`
              : 'There are no CVs from other roles at this school yet.'}
          />
        )}

        {done && (
          <div role="status" style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'var(--status-success-bg)', color: 'var(--status-success-text)', fontSize: 'var(--fs-body-md)' }}>
            {done.added.length ? `Added ${plural(done.added.length, 'person', 'people')} as new applications, waiting for CV screening.` : 'Nobody new was added.'}
            {done.alreadyOnJob.length ? ` ${plural(done.alreadyOnJob.length, 'person was', 'people were')} already on this vacancy.` : ''}
            {' '}{done.told?.told?.length
              ? `${plural(done.told.told.length, 'person was', 'people were')} emailed that they are being considered.${done.told.notTold?.length ? ` ${done.told.notTold.length} not emailed (${done.told.notTold[0].why}).` : ''}`
              : 'Nobody has been messaged.'}
          </div>
        )}

        {!loading && !error && matches.length > 0 && (
          <>
            <div style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)', marginBottom: 8 }}>
              {plural(matches.length, 'good fit', 'good fits')} from {plural(result.considered, 'CV', 'CVs')} checked
              {result.fromCache ? ' (some scores are from earlier today)' : ''}.
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              {matches.map((m) => {
                const isAdded = addedIds.has(m.resumeId);
                return (
                  <Card key={m.resumeId} padding="14px 16px" style={{ boxShadow: 'none' }}>
                    {/* The whole row picks the person: the design-system
                        Checkbox draws the box, the row carries the role. */}
                    <div
                      role="checkbox"
                      aria-checked={picked.has(m.resumeId)}
                      aria-disabled={isAdded}
                      aria-label={`Pick ${m.candidateName}`}
                      tabIndex={isAdded ? -1 : 0}
                      onClick={() => { if (!isAdded) toggle(m.resumeId); }}
                      onKeyDown={(e) => { if (!isAdded && (e.key === ' ' || e.key === 'Enter')) { e.preventDefault(); toggle(m.resumeId); } }}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: isAdded ? 'default' : 'pointer' }}
                    >
                      <div style={{ paddingTop: 2 }}>
                        <Checkbox checked={picked.has(m.resumeId)} disabled={isAdded} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <strong style={{ fontSize: 'var(--fs-body-md)', color: 'var(--text-1)' }}>{m.candidateName}</strong>
                          {isAdded && <StatusPill tone="neutral">On this vacancy</StatusPill>}
                        </div>
                        <div style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>Applied for {m.appliedFor}</div>
                        <Reasons items={m.reasons} />
                      </div>
                      <StatusPill tone={toneOf(m.score)} dot={false}><span className="num">{m.score}% fit</span></StatusPill>
                    </div>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}

/* The button on the vacancy's page. */
export function ExistingCvsButton({ jobId, jobTitle, onAdded }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="eq-ds">
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>Also check our existing CVs</Button>
      <ExistingCvMatches jobId={jobId} jobTitle={jobTitle} open={open} onClose={() => setOpen(false)} onAdded={onAdded} />
    </span>
  );
}

/* Right after a JD is created: the same check, offered once. */
export function ExistingCvsOffer({ jobId, jobTitle, onDismiss, onAdded }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="eq-ds" style={{ marginBottom: 16 }}>
      <Card padding="16px 20px" style={{ boxShadow: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <strong style={{ fontSize: 'var(--fs-body-md)', color: 'var(--text-1)' }}>Also check our existing CVs?</strong>
            <div style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--text-2)' }}>
              See who has already applied to this school for another role and fits this one. You choose whom to add; nobody is messaged.
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onDismiss}>Not now</Button>
          <Button size="sm" onClick={() => setOpen(true)}>Also check our existing CVs</Button>
        </div>
      </Card>
      <ExistingCvMatches jobId={jobId} jobTitle={jobTitle} open={open} onClose={() => setOpen(false)} onAdded={onAdded} />
    </div>
  );
}
